import React, { useState, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import AnnouncementService from '../../../services/AnnouncementService';
import '../styles/Announcement.css';

const Announcement = ({ onClose, darkMode, opacity, blur, baseColor = null }) => {
    const [announcements, setAnnouncements] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        loadAnnouncements();
    }, []);

    const loadAnnouncements = async () => {
        try {
            const activeAnnouncements = await AnnouncementService.getActiveAnnouncements();
            if (activeAnnouncements && activeAnnouncements.length > 0) {
                setAnnouncements(activeAnnouncements);
            }
        } catch (error) {
            console.error('Failed to load announcements:', error);
        }
    };

    const handleNext = () => {
        if (isAnimating) return;
        setIsAnimating(true);
        setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % announcements.length);
            setIsAnimating(false);
        }, 300); // Match the animation duration
    };

    const handlePrevious = () => {
        if (isAnimating) return;
        setIsAnimating(true);
        setTimeout(() => {
            setCurrentIndex((prev) => (prev - 1 + announcements.length) % announcements.length);
            setIsAnimating(false);
        }, 300); // Match the animation duration
    };

    // Convert hex to RGB for background tinting
    const hexToRgb = (hex) => {
        if (!hex) return null;
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
        
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };
    
    const getBackgroundColor = () => {
        if (!baseColor) {
            return darkMode ? `rgba(45, 45, 45, ${opacity})` : `rgba(255, 255, 255, ${opacity})`;
        }
        
        const rgb = hexToRgb(baseColor);
        if (!rgb) return darkMode ? `rgba(45, 45, 45, ${opacity})` : `rgba(255, 255, 255, ${opacity})`;
        
        if (darkMode) {
            return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
        } else {
            const lightFactor = 0.35;
            const lightR = Math.round(rgb.r * lightFactor + 255 * (1 - lightFactor));
            const lightG = Math.round(rgb.g * lightFactor + 255 * (1 - lightFactor));
            const lightB = Math.round(rgb.b * lightFactor + 255 * (1 - lightFactor));
            return `rgba(${lightR}, ${lightG}, ${lightB}, ${opacity})`;
        }
    };

    // If no announcements, show static content
    if (!announcements.length) {
        const currentDate = new Date();
        const day = currentDate.getDate();
        const month = currentDate.toLocaleString('default', { month: 'short' });
        const weekday = currentDate.toLocaleString('default', { weekday: 'long' });

        return (
            <div 
                className="announcement-container"
                style={{
                    '--local-opacity': opacity,
                    '--local-blur': blur,
                    backgroundColor: getBackgroundColor()
                }}
            >
                <div className="announcement-content">
                    <div className="calendar-section">
                        <div className="calendar">
                            <div className="calendar-day">{day}</div>
                            <div className="calendar-month">{month}</div>
                            <div className="calendar-weekday">{weekday}</div>
                        </div>
                    </div>
                    <div className="announcement-text">
                        <div className="announcement-text-content">
                            <h4>Welcome to Your Dashboard!</h4>
                            <p>Here you can manage your organization's tasks, events, and more.</p>
                        </div>
                    </div>
                    <button className="announcement-close" onClick={onClose}>
                        <span>×</span>
                    </button>
                </div>
            </div>
        );
    }

    // Show announcement from database
    const currentAnnouncement = announcements[currentIndex];
    const date = new Date(currentAnnouncement.createdAt);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const weekday = date.toLocaleString('default', { weekday: 'long' });

    return (
        <div 
            className="announcement-container"
            style={{
                '--local-opacity': opacity,
                '--local-blur': blur,
                backgroundColor: getBackgroundColor()
            }}
        >
            <button className="announcement-close" onClick={onClose}>
                <span>×</span>
            </button>
            
            <div className="announcement-content">
                <div className="calendar-section">
                    <div className="calendar">
                        <div className="calendar-day">{day}</div>
                        <div className="calendar-month">{month}</div>
                        <div className="calendar-weekday">{weekday}</div>
                    </div>
                </div>
                <div className="announcement-text">
                    <div className={`announcement-text-content ${isAnimating ? 'fade-out' : ''}`}>
                        <h3>{currentAnnouncement.title}</h3>
                        <p>{currentAnnouncement.text}</p>
                    </div>
                </div>
            </div>
            
            {announcements.length > 1 && (
                <div className="nav-buttons">
                    <button 
                        className="nav-button"
                        onClick={handlePrevious}
                        disabled={isAnimating}
                    >
                        <FaChevronLeft />
                    </button>
                    <button 
                        className="nav-button"
                        onClick={handleNext}
                        disabled={isAnimating}
                    >
                        <FaChevronRight />
                    </button>
                </div>
            )}
        </div>
    );
};

export default Announcement; 